import { useQuery } from '@apollo/client';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import OrdatAdvancedFields from './OrdatAdvancedFields.tsx';
import {
    FORM_DEFAULT_VALUES,
    normalizeOrderPushMode,
    parseJsonStringObject,
    type OrdatFormData,
    type OrdatIntegration,
} from './types.ts';
import CheckboxGroup from '../../../core/components/CheckboxGroup.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import { SecretField } from '../../../core/components/Secret.tsx';

export interface OrdatIntegrationFormProps {
    integration?: OrdatIntegration;
    onSubmit: (data: OrdatFormData) => Promise<void>;
    formId?: string;
}

/** Shared create/edit form for the ORDAT.ERP connector. */
const OrdatIntegrationForm = ({
    integration,
    onSubmit,
    formId = 'ordat-integration-form',
}: OrdatIntegrationFormProps) => {
    const { t } = useTranslation('integrations');

    const form = useForm<OrdatFormData>({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    useEffect(() => {
        if (!integration) return;

        form.reset({
            ...FORM_DEFAULT_VALUES,
            name: integration.name,
            baseUrl: integration.baseUrl,
            username: integration.username,
            // never prefilled: the backend does not return the stored password
            password: '',
            proxy: integration.proxy ?? '',
            // never prefilled either; hasCaBundle tells whether one is stored
            caBundle: '',
            companyName: integration.companyName ?? '',
            businessLine: integration.businessLine ?? '',
            businessSubLine: integration.businessSubLine ?? '',
            cultureName: integration.cultureName || FORM_DEFAULT_VALUES.cultureName,
            orderPushMode: normalizeOrderPushMode(integration.orderPushMode),
            defaultShipmentType: integration.defaultShipmentType ?? '',
            headerTextEnabled: integration.headerTextEnabled,
            headerTextContextId: integration.headerTextContextId ?? '',
            itemTextEnabled: integration.itemTextEnabled,
            itemTextContextId: integration.itemTextContextId ?? '',
            itemAdditionalTextContextId:
                integration.itemAdditionalTextContextId === null ||
                integration.itemAdditionalTextContextId === undefined
                    ? ''
                    : String(integration.itemAdditionalTextContextId),
            fallbackPartNumber: integration.fallbackPartNumber ?? '',
            sellablePartsOnly: integration.sellablePartsOnly,
            attachSourceDocuments: integration.attachSourceDocuments,
            priceCheckLowerThresholdPercentage: String(integration.priceCheckLowerThresholdPercentage ?? 0),
            priceCheckUpperThresholdPercentage: String(integration.priceCheckUpperThresholdPercentage ?? 0),
            integrationParameters: parseJsonStringObject(integration.integrationParameters),
            useOutputMapping: integration.useOutputMapping,
            orderChannelIds: integration.orderChannels.map((channel) => channel.id),
            rfqChannelIds: integration.rfqChannels.map((channel) => channel.id),
        });
    }, [integration, form]);

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });
    const orderChannels = channelData?.orderProcessingChannels || [];
    const rfqChannels = channelData?.rfqProcessingChannels || [];

    return (
        <form
            className="max-w-3xl w-full flex flex-col gap-6"
            onSubmit={form.handleSubmit((data) => onSubmit(data))}
            id={formId}
        >
            <Controller
                name="name"
                control={form.control}
                rules={{ required: t('ordat.form.requiredError') }}
                render={({ field }) => (
                    <FormField label={t('ordat.form.name')} error={formErrors.name} required>
                        <StringField {...field} />
                    </FormField>
                )}
            />

            <Controller
                name="baseUrl"
                control={form.control}
                rules={{ required: t('ordat.form.requiredError') }}
                render={({ field }) => (
                    <FormField
                        label={t('ordat.form.baseUrl')}
                        error={formErrors.baseUrl}
                        helpText={t('ordat.form.baseUrlHelpText')}
                        required
                    >
                        <StringField {...field} placeholder="https://<host>/webservice" />
                    </FormField>
                )}
            />

            <Controller
                name="username"
                control={form.control}
                rules={{ required: t('ordat.form.requiredError') }}
                render={({ field }) => (
                    <FormField label={t('ordat.form.username')} error={formErrors.username} required>
                        <StringField {...field} />
                    </FormField>
                )}
            />

            <Controller
                name="password"
                control={form.control}
                rules={{ required: integration ? false : t('ordat.form.requiredError') }}
                render={({ field }) => (
                    <FormField
                        label={t('ordat.form.password')}
                        error={formErrors.password}
                        helpText={integration ? t('secretUpdateOnly') : undefined}
                        required={!integration}
                    >
                        <SecretField
                            {...field}
                            secretName="password"
                            secretId={integration?.id}
                            placeholder={integration ? t('ordat.form.passwordUnchangedPlaceholder') : undefined}
                        />
                    </FormField>
                )}
            />

            <Controller
                name="companyName"
                control={form.control}
                render={({ field }) => (
                    <FormField
                        label={t('ordat.form.companyName')}
                        error={formErrors.companyName}
                        helpText={t('ordat.form.companyNameHelpText')}
                    >
                        <StringField {...field} />
                    </FormField>
                )}
            />

            <div className="flex gap-4">
                <Controller
                    name="businessLine"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            className="flex-1"
                            label={t('ordat.form.businessLine')}
                            error={formErrors.businessLine}
                            helpText={t('ordat.form.businessLineHelpText')}
                        >
                            <StringField {...field} maxLength={1} />
                        </FormField>
                    )}
                />

                <Controller
                    name="businessSubLine"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            className="flex-1"
                            label={t('ordat.form.businessSubLine')}
                            error={formErrors.businessSubLine}
                            helpText={t('ordat.form.businessSubLineHelpText')}
                        >
                            <StringField {...field} maxLength={1} />
                        </FormField>
                    )}
                />
            </div>

            <div className="text-lg font-semibold mt-2">{t('ordat.form.header.integrationOrders')}</div>

            <Controller
                name="orderChannelIds"
                control={form.control}
                render={({ field }) => (
                    <FormField label={t('ordat.form.orderChannelIds')} error={formErrors.orderChannelIds}>
                        <CheckboxGroup
                            {...field}
                            options={orderChannels.map((channel) => ({ value: channel.id, label: channel.name }))}
                            onValueChange={(value) => field.onChange({ target: { value } })}
                        />
                    </FormField>
                )}
            />

            <div className="text-lg font-semibold mt-2">{t('ordat.form.header.integrationRFQs')}</div>

            <Controller
                name="rfqChannelIds"
                control={form.control}
                render={({ field }) => (
                    <FormField label={t('ordat.form.rfqChannelIds')} error={formErrors.rfqChannelIds}>
                        <CheckboxGroup
                            {...field}
                            options={rfqChannels.map((channel) => ({ value: channel.id, label: channel.name }))}
                            onValueChange={(value) => field.onChange({ target: { value } })}
                        />
                    </FormField>
                )}
            />

            <OrdatAdvancedFields form={form} integration={integration} />
        </form>
    );
};

export default OrdatIntegrationForm;
